export const EDUCATIONAL_RESOURCES_LABEL = 'cryptoasset educational resources'

export type CopyNode = string | { link: string }
export type CopyParagraph = CopyNode[]

export interface AssessmentFailureCopy {
  paragraphs: CopyParagraph[]
  primaryButton: 'retry' | 'returnToApp'
  showLeaveButton: boolean
}

export interface AssessmentFailureCopyInput {
  terminal: boolean
  canRetryNow: boolean
  attempt: number
  maxAttempts: number | null
  delaysHours: number[]
  canReturnToApp: boolean
  minutesUntilRetry?: number
}

const closingLine = (canReturnToApp: boolean): CopyParagraph =>
  canReturnToApp
    ? ['Please press the button below to return to the app.']
    : ['You can now close this page and return to the app']

const resourcesLine = (suffix: string): CopyParagraph => [
  'Before you make this new attempt, why not check out our ',
  { link: EDUCATIONAL_RESOURCES_LABEL },
  suffix,
]

export const minutesUntil = (target: Date, now: number): number =>
  Math.max(0, Math.ceil((target.getTime() - now) / 60_000))

const formatDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const parts: string[] = []
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`)
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`)
  }
  if (parts.length === 0) {
    parts.push('0 minutes')
  }
  return parts.join(' and ')
}

export const buildAssessmentFailureCopy = (
  input: AssessmentFailureCopyInput,
): AssessmentFailureCopy => {
  const {
    terminal,
    canRetryNow,
    attempt,
    maxAttempts,
    delaysHours,
    canReturnToApp,
    minutesUntilRetry = 0,
  } = input

  if (terminal) {
    return {
      paragraphs: [
        ['Some of your answers were incorrect.'],
        [
          'Unfortunately, you’ve reached the maximum number of appropriateness assessment attempts. As a result, we are unable to provide you with our services at this time.',
        ],
        closingLine(canReturnToApp),
      ],
      primaryButton: 'returnToApp',
      showLeaveButton: false,
    }
  }

  if (canRetryNow) {
    return {
      paragraphs: [
        ['Oops, some of your answers were incorrect.'],
        [
          'Before you start trading, we must make sure that digital assets are the right investment for you.',
        ],
        [
          'If you wish, you can give it another try by completing a new assessment.',
        ],
        resourcesLine(
          ' to ensure you are comfortable with your crypto knowledge?',
        ),
      ],
      primaryButton: 'retry',
      showLeaveButton: true,
    }
  }

  const lastIdx = delaysHours.length - 1
  const currentWait = delaysHours[Math.min(attempt - 1, lastIdx)] ?? 0
  const penultimate = maxAttempts !== null && attempt + 1 === maxAttempts
  const nextWait = delaysHours[Math.min(attempt, lastIdx)] ?? currentWait
  const another = nextWait === currentWait ? 'another ' : ''

  const middleLine: CopyParagraph = penultimate
    ? ['Please note that this will be your last chance to pass the assessment.']
    : [
        `If you don’t pass on your next attempt, you will have to wait ${another}${nextWait} hours to try again.`,
      ]

  return {
    paragraphs: [
      ['Oops, some of your answers were incorrect.'],
      [
        `You can try again in ${formatDuration(minutesUntilRetry)} time. This will give you the time to really consider if investment in crypto is for you and to seek financial advice.`,
      ],
      middleLine,
      resourcesLine(
        ' to ensure you are comfortable with your crypto knowledge while you wait?',
      ),
      closingLine(canReturnToApp),
    ],
    primaryButton: 'returnToApp',
    showLeaveButton: false,
  }
}
